/**
 * Billing state and mandate capture — the thin typed seam between the UI and the
 * two Edge Functions (`stripe-signup`, `stripe-webhook`).
 *
 * StaySuite does not bill. Payment details are captured up front, nothing is charged
 * during the 30 day trial, and after it the AUVISO CRM raises the invoice into
 * sevDesk while a separate cron collects it through Stripe. Everything here exists
 * to get a *mandate* on file and to let the UI say honestly where that stands.
 *
 * The vocabulary (`mandate_status`, `default_payment_method`, `payment_method_type`,
 * `mandate_setup_at`) is the AUVISO CRM's, on purpose — the same mandate should not
 * have two names in two systems.
 *
 * Nothing in this module reaches for a Stripe publishable key or Stripe.js. Confirming
 * the SetupIntent is the view layer's job; this module hands it a client secret.
 */

import type { SupabaseClient } from '@supabase/supabase-js'

/* ------------------------------------------------------------------- types */

/**
 * Die Vertragsform, nicht der Betrag.
 *
 * `monthly` = Monatsabo mit einem Monat Mindestlaufzeit, `yearly` = zwölf
 * Monate im Voraus. Was das kostet, hängt am Paket und steht in `plans` —
 * hier absichtlich nicht, seit Vorgang 010 den Preis auf eine einzige Stelle
 * zusammengeführt hat. Der Betrag kommt über `src/data/pakete.ts`.
 */
export type ContractTerm = 'monthly' | 'yearly'

export type PaymentMethodType = 'card' | 'sepa_debit'

/**
 * `requested` and `active` are the two the AUVISO CRM already uses; the others name
 * states Stripe can produce that the CRM has not had to represent yet.
 */
export type MandateStatus = 'none' | 'requested' | 'active' | 'failed' | 'revoked'

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'paused'

/** The billing half of the `subscriptions` row. */
export interface BillingState {
  tenant_id: string
  plan_code: string
  pay_channel: 'stripe' | 'apple' | 'google' | null
  status: SubscriptionStatus
  contract_term: ContractTerm
  trial_starts_at: string | null
  trial_ends_at: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  stripe_customer_id: string | null
  stripe_setup_intent_id: string | null
  default_payment_method: string | null
  payment_method_type: PaymentMethodType | null
  mandate_status: MandateStatus
  mandate_requested_at: string | null
  mandate_setup_at: string | null
  mandate_last_error: string | null
  livemode: boolean
  crm_customer_code: string | null
  crm_synced_at: string | null
}

/**
 * What `stripe-signup` returns on success: the hosted Stripe Checkout page to send
 * the landlord to. Checkout collects card or SEPA and shows the SEPA mandate text
 * in their language — none of which we should be writing ourselves.
 */
export interface MandateSetup {
  url: string
  checkout_session_id: string
  customer_id: string
  customer_code: string | null
  contract_term: ContractTerm
  trial_starts_at: string
  trial_ends_at: string
  livemode: boolean
  /** False when the AUVISO customer could not be created; signup still proceeds. */
  crm_ok: boolean
}

/** What `stripe-signup` returns for `action: 'sync'` after Checkout comes back. */
export interface MandateSyncResult {
  mandate_status: MandateStatus
  setup_intent_status: string
  payment_method_type: PaymentMethodType | null
  trial_ends_at: string
}

/**
 * Every failure the functions can produce, by name. The UI should switch on these
 * rather than on a message: `stripe_secret_key_missing` is an operator's problem,
 * `tenant_has_no_billing_email` is the landlord's, and they read very differently.
 */
export type BillingErrorCode =
  | 'no_backend'
  | 'missing_authorization'
  | 'not_authenticated'
  | 'no_tenant_membership'
  | 'not_a_member_of_tenant'
  | 'role_may_not_manage_billing'
  | 'subscription_not_found'
  | 'invalid_contract_term'
  | 'tenant_has_no_billing_email'
  | 'tenant_has_no_billing_name'
  | 'stripe_secret_key_missing'
  | 'stripe_error'
  | 'server_misconfigured'
  | 'unexpected_error'

export class BillingError extends Error {
  constructor(
    readonly code: BillingErrorCode | string,
    message?: string,
    readonly detail?: string | null,
  ) {
    super(message ?? code)
    this.name = 'BillingError'
  }
}

/* --------------------------------------------------------------- pure logic */

const MANDATE_STATUSES: MandateStatus[] = ['none', 'requested', 'active', 'failed', 'revoked']
const PAYMENT_METHOD_TYPES: PaymentMethodType[] = ['card', 'sepa_debit']

export function isMandateStatus(v: unknown): v is MandateStatus {
  return typeof v === 'string' && (MANDATE_STATUSES as string[]).includes(v)
}

export function isPaymentMethodType(v: unknown): v is PaymentMethodType {
  return typeof v === 'string' && (PAYMENT_METHOD_TYPES as string[]).includes(v)
}

/** A mandate is only usable if it is active AND names the method that carries it. */
export function hasUsableMandate(s: Pick<BillingState, 'mandate_status' | 'default_payment_method'>): boolean {
  return s.mandate_status === 'active' && !!s.default_payment_method
}

/** Whole days left in the trial; 0 once it is over, null when there is no trial. */
export function trialDaysLeft(s: Pick<BillingState, 'trial_ends_at'>, now: Date = new Date()): number | null {
  if (!s.trial_ends_at) return null
  const ms = new Date(s.trial_ends_at).getTime() - now.getTime()
  return ms <= 0 ? 0 : Math.ceil(ms / 86_400_000)
}

/**
 * What the landlord needs to be told, in one word. Deliberately not the same thing
 * as `status`: a trial with no mandate and a trial with one look identical in the
 * database column but are opposite situations for the business.
 */
export type BillingStage =
  | 'trial_no_mandate'   // trial running, payment details still missing
  | 'trial_pending'      // SetupIntent created, not confirmed (SEPA can take a moment)
  | 'trial_mandate_ok'   // trial running, mandate on file — the happy path
  | 'trial_failed'       // the setup was refused; nothing will be collectable
  | 'trial_expired'      // trial over, the CRM has not converted it yet
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'paused'

/**
 * Warum die Einrichtung gescheitert ist — als Satz, nicht als Kennung.
 *
 * `mandate_last_error` wird seit jeher geladen und war bis zum 03.08.2026
 * **nirgends sichtbar**. Der Grund stand in der Datenbank, und der Vermieter las
 * „Zahlungsdaten fehlen" — dieselbe Auskunft wie jemand, der es noch nie
 * versucht hat.
 *
 * Bei `haus-am-h-hlenbach` lautet der Wert seit dem 27.07.2026
 * `checkout_session_expired_or_canceled`: Der Bezahlvorgang wurde nicht zu Ende
 * geführt. **Das ist kein technischer Defekt, sondern ein Abbruch** — und wer
 * das weiss, weiss auch, was zu tun ist. Wer es nicht weiss, sucht den Fehler
 * bei sich oder beim Programm.
 *
 * DIE KENNUNG SELBST WIRD NIE ANGEZEIGT. Sie stammt teils von Stripe, ist
 * englisch, und ein Vermieter kann mit `setup_intent_authentication_failure`
 * nichts anfangen. Ein unbekannter Wert fällt deshalb auf einen allgemeinen Satz
 * zurück, statt roh durchgereicht zu werden — und der allgemeine Satz sagt
 * trotzdem, was zu tun ist.
 */
export function mandatFehlerSatz(grund: string | null, de: boolean): string {
  switch (grund) {
    case 'checkout_session_expired_or_canceled':
      return de
        ? 'Der Bezahlvorgang wurde nicht zu Ende geführt — das Fenster war abgelaufen oder wurde geschlossen.'
        : 'The payment setup was not completed — the window had expired or was closed.'
    case 'setup_intent_authentication_failure':
      return de
        ? 'Ihre Bank hat die Bestätigung abgelehnt.'
        : 'Your bank declined the confirmation.'
    case 'payment_method_declined':
      return de
        ? 'Das Zahlungsmittel wurde abgelehnt.'
        : 'The payment method was declined.'
    case 'mandate_revoked':
      return de
        ? 'Das Lastschriftmandat wurde widerrufen.'
        : 'The direct debit mandate was revoked.'
    default:
      return de
        ? 'Die Einrichtung wurde nicht abgeschlossen.'
        : 'The setup was not completed.'
  }
}

export function billingStage(s: BillingState, now: Date = new Date()): BillingStage {
  if (s.status === 'active') return 'active'
  if (s.status === 'past_due') return 'past_due'
  if (s.status === 'canceled') return 'canceled'
  if (s.status === 'paused') return 'paused'

  const left = trialDaysLeft(s, now)
  if (left === 0) return 'trial_expired'
  if (s.mandate_status === 'failed' || s.mandate_status === 'revoked') return 'trial_failed'
  if (hasUsableMandate(s)) return 'trial_mandate_ok'
  if (s.mandate_status === 'requested') return 'trial_pending'
  return 'trial_no_mandate'
}

/*
 * Hier stand bis zum 01.08.2026 `contractPrice(term)` mit zwei fest
 * verdrahteten Beträgen — der fünfte Preisort, den nicht einmal die
 * Aufzählung in der Anforderung kannte, weil ihn außer seinem eigenen Test
 * niemand aufrief. Ersetzt durch `preisJePeriode()` in `src/data/pakete.ts`,
 * das den Betrag aus `plans` liest, statt ihn zu kennen.
 */

/* ------------------------------------------------------------------- reads */

const BILLING_COLUMNS = [
  'tenant_id', 'plan_code', 'pay_channel', 'status', 'contract_term',
  'trial_starts_at', 'trial_ends_at', 'current_period_end', 'cancel_at_period_end',
  'stripe_customer_id', 'stripe_setup_intent_id',
  'default_payment_method', 'payment_method_type',
  'mandate_status', 'mandate_requested_at', 'mandate_setup_at', 'mandate_last_error',
  'livemode', 'crm_customer_code', 'crm_synced_at',
].join(', ')

function normalise(row: Record<string, unknown>): BillingState {
  return {
    tenant_id: String(row.tenant_id),
    plan_code: String(row.plan_code),
    pay_channel: row.pay_channel === 'stripe' || row.pay_channel === 'apple' || row.pay_channel === 'google' ? row.pay_channel : null,
    status: String(row.status) as SubscriptionStatus,
    contract_term: row.contract_term === 'yearly' ? 'yearly' : 'monthly',
    trial_starts_at: (row.trial_starts_at as string | null) ?? null,
    trial_ends_at: (row.trial_ends_at as string | null) ?? null,
    current_period_end: (row.current_period_end as string | null) ?? null,
    cancel_at_period_end: row.cancel_at_period_end === true,
    stripe_customer_id: (row.stripe_customer_id as string | null) ?? null,
    stripe_setup_intent_id: (row.stripe_setup_intent_id as string | null) ?? null,
    default_payment_method: (row.default_payment_method as string | null) ?? null,
    payment_method_type: isPaymentMethodType(row.payment_method_type) ? row.payment_method_type : null,
    mandate_status: isMandateStatus(row.mandate_status) ? row.mandate_status : 'none',
    mandate_requested_at: (row.mandate_requested_at as string | null) ?? null,
    mandate_setup_at: (row.mandate_setup_at as string | null) ?? null,
    mandate_last_error: (row.mandate_last_error as string | null) ?? null,
    livemode: row.livemode === true,
    crm_customer_code: (row.crm_customer_code as string | null) ?? null,
    crm_synced_at: (row.crm_synced_at as string | null) ?? null,
  }
}

/**
 * The signed-in tenant's billing state. RLS restricts `subscriptions` to the caller's
 * own tenants, so this cannot read anybody else's — the `tenant_id` filter is a
 * convenience, not the security boundary.
 */
export async function loadBillingState(
  sb: SupabaseClient,
  tenantId: string,
): Promise<BillingState | null> {
  const { data, error } = await sb
    .from('subscriptions').select(BILLING_COLUMNS).eq('tenant_id', tenantId).maybeSingle()
  if (error) throw new BillingError('read_failed', error.message)
  return data ? normalise(data as unknown as Record<string, unknown>) : null
}

/* ------------------------------------------------------------------ writes */

export interface StartMandateSetupOptions {
  contractTerm: ContractTerm
  /** Only needed if a user ever belongs to more than one tenant. */
  tenantId?: string
}

/** Shared plumbing: invoke the function and turn its named errors back into errors. */
async function invokeSignup<T>(
  sb: SupabaseClient | null,
  body: Record<string, unknown>,
): Promise<T> {
  if (!sb) throw new BillingError('no_backend', 'Demo mode has no billing backend.')

  const { data, error } = await sb.functions.invoke('stripe-signup', { body })

  if (error) {
    // functions.invoke hides the body of a non-2xx behind a generic error, and the
    // named code is the whole point of these functions — so dig it back out.
    const res = (error as { context?: Response }).context
    if (res && typeof res.json === 'function') {
      const parsed = await res.json().catch(() => null) as
        { error?: string; detail?: string; stripe_code?: string } | null
      if (parsed?.error) {
        throw new BillingError(parsed.error, parsed.error, parsed.detail ?? parsed.stripe_code ?? null)
      }
    }
    throw new BillingError('unexpected_error', error.message)
  }

  const payload = data as T & { error?: string }
  if (payload?.error) throw new BillingError(payload.error)
  return payload as T
}

/**
 * Ask `stripe-signup` for a hosted Checkout page in setup mode. Nothing is charged:
 * the landlord confirms card or SEPA on Stripe's own page, and the mandate is read
 * back from Stripe afterwards rather than believed from the browser. That asymmetry
 * is deliberate — a browser can lie, and a SEPA confirmation is not always instant.
 */
export async function startMandateSetup(
  sb: SupabaseClient | null,
  opts: StartMandateSetupOptions,
): Promise<MandateSetup> {
  const body = await invokeSignup<MandateSetup>(sb, {
    action: 'start',
    contract_term: opts.contractTerm,
    tenant_id: opts.tenantId,
  })
  if (!body?.url) throw new BillingError('unexpected_error', 'No checkout URL returned.')
  return body
}

/**
 * Called when Checkout sends the landlord back. Reads the session from Stripe,
 * writes the mandate onto the subscription, and tells the AUVISO CRM.
 */
export async function syncMandate(
  sb: SupabaseClient | null,
  sessionId: string | null,
  tenantId?: string,
): Promise<MandateSyncResult> {
  return invokeSignup<MandateSyncResult>(sb, {
    action: 'sync',
    ...(tenantId ? { tenant_id: tenantId } : {}),
    ...(sessionId ? { session_id: sessionId } : {}),
  })
}

/**
 * Poll for the webhook's verdict after the browser has confirmed the SetupIntent.
 * SEPA is not instant, so the UI needs a "we are waiting" state rather than a
 * success it cannot back up.
 */
export async function waitForMandate(
  sb: SupabaseClient,
  tenantId: string,
  opts: { attempts?: number; intervalMs?: number; sleep?: (ms: number) => Promise<void> } = {},
): Promise<BillingState | null> {
  const attempts = opts.attempts ?? 10
  const intervalMs = opts.intervalMs ?? 1500
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)))

  let last: BillingState | null = null
  for (let i = 0; i < attempts; i++) {
    last = await loadBillingState(sb, tenantId)
    if (last && (hasUsableMandate(last) || last.mandate_status === 'failed')) return last
    if (i < attempts - 1) await sleep(intervalMs)
  }
  return last
}
